/**
 * Feature flags that can be checked against account limits
 */
export const FEATURES = {
    EXPORT: 'exportEnabled',
    SHARE: 'shareEnabled',
    ADVANCED_ANALYTICS: 'advancedAnalytics',
    ML_INSIGHTS: 'mlInsights',
    SLACK: 'slackIntegration',
    API_ACCESS: 'apiAccess',
    BYOK: 'byokEnabled',
}

/**
 * Human-readable tier labels
 */
export const TIER_LABELS = {
    free_trial: 'Free Trial',
    individual: 'Individual',
    team: 'Team',
    enterprise: 'Enterprise',
    appsumo_individual: 'AppSumo Tier 1',
    appsumo_team_small: 'AppSumo Tier 2',
    appsumo_team_unlimited: 'AppSumo Tier 3',
}

/**
 * Tier pricing for display
 */
export const TIER_PRICES = {
    free_trial: 'Free for 30 days',
    individual: '$99/month',
    team: '$199/month per seat',
    enterprise: 'Custom pricing',
}

/**
 * Check if a feature is available for the current account.
 *
 * @param {string} feature - The feature to check (use FEATURES constants)
 * @param {Object} account - The account object from workspaceMetadata
 * @returns {Object} { hasAccess: boolean, requiresTier: string|null }
 */
export const checkFeatureAccess = (feature, account) => {
    const hasAccess = account?.limits?.[feature] ?? false

    return {
        hasAccess,
        requiresTier: !hasAccess ? getRequiredTier(feature) : null,
    }
}

/**
 * Get the minimum tier required for a feature.
 *
 * @param {string} feature - The feature flag name
 * @returns {string} Tier name ('individual' or 'team')
 */
const getRequiredTier = (feature) => {
    // Map features to minimum required tier
    const tierMap = {
        exportEnabled: 'individual',
        shareEnabled: 'individual',
        advancedAnalytics: 'team',
        mlInsights: 'team',
        slackIntegration: 'team',
        apiAccess: 'team',
    }

    return tierMap[feature] || 'team'
}

/**
 * Calculate usage percentage.
 *
 * @param {number} used - Current usage
 * @param {number} limit - Maximum limit
 * @returns {number} Percentage (0-100)
 */
export const getUsagePercentage = (used, limit) => {
    if (!limit || limit === 0) return 0
    return Math.min(Math.round((used / limit) * 100), 100)
}

/**
 * Get usage status indicator.
 *
 * @param {number} used - Current usage
 * @param {number} limit - Maximum limit
 * @returns {string} Status: 'normal' | 'warning' | 'critical'
 */
export const getUsageStatus = (used, limit) => {
    const percentage = getUsagePercentage(used, limit)

    if (percentage >= 100) return 'critical'
    if (percentage >= 80) return 'warning'
    return 'normal'
}

/**
 * Check if account is subscribed to a paid plan.
 *
 * @param {Object} account - The account object from workspaceMetadata
 * @returns {boolean} True if account has an active subscription
 */
export const isSubscribed = (account) => {
    return !!account?.chargebeeCustomerId || account?.accountType === 'enterprise'
}

/**
 * Check if account can upgrade to a specific tier.
 *
 * @param {Object} account - The account object
 * @param {string} targetTier - Tier to check ('individual', 'team', 'enterprise')
 * @returns {boolean} True if upgrade is available
 */
export const canUpgradeTo = (account, targetTier) => {
    const currentTier = account?.accountType

    // Can't upgrade to same tier
    if (currentTier === targetTier) return false

    // AppSumo users can upgrade to any tier
    if (account?.isAppSumo) return true

    // Tier hierarchy
    const tierOrder = ['free_trial', 'individual', 'team', 'enterprise']
    const currentIndex = tierOrder.indexOf(currentTier)
    const targetIndex = tierOrder.indexOf(targetTier)

    // Can only upgrade to higher tiers
    return targetIndex > currentIndex
}
